// src/pages/EventPage.js
import React from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import HeroBanner from '../components/HeroBanner';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import { Box, Typography, Link as MuiLink } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import { eventItems } from '../data/SiteData';

const EventPage = () => {
  const { eventId } = useParams();

  // Find the event by eventId
  const event = eventItems.find((e) => e.eventId === eventId);

  if (!event) {
    return (
      <>
        <Navbar />
        <HeroBanner
          heroTitle="Events"
          heroDescription="Find out about all the latest events."
          imageUrl="/images/digiTwins.png"
          height="165px"
        />
        <Breadcrumb
          items={[
            { title: 'Home', path: '/' },
            { title: 'News & Events', path: '/news-events' },
            { title: 'Events', path: '/news-events/events' },
            { title: 'Event not found', path: '#' },
          ]}
        />
        <Box sx={{ padding: '32px', maxWidth: '1300px', margin: '0 auto' }}>
          <Typography variant="h6">Event not found</Typography>
        </Box>
        <Footer />
      </>
    );
  }

  const truncatedTitle =
    event.title.length > 30 ? event.title.slice(0, 30) + '...' : event.title;

  const formattedDate = event.endDate
    ? `${event.startDate} - ${event.endDate}`
    : event.startDate;

  const locationText = event.location ? `, ${event.location.toUpperCase()}` : '';
  const eventTypeText = event.eventType ? event.eventType.toUpperCase() : '';

  return (
    <>
      <Navbar />
      <HeroBanner
        heroTitle="Events"
        heroDescription="Find out about all the latest events."
        imageUrl="/images/digiTwins.png"
        height="165px"
      />
      <Breadcrumb
        items={[
          { title: 'Home', path: '/' },
          { title: 'News & Events', path: '/news-events' },
          { title: 'Events', path: '/news-events/events' },
          { title: truncatedTitle, path: '#' },
        ]}
      />

      {/* Main content wrapper */}
      <Box sx={{ padding: '32px', maxWidth: '1300px', margin: '0 auto' }}>
        {/* Section 1: Image + Event Info */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 3,
            mb: 4,
          }}
        >
          {event.image && (
            <MuiLink href={event.link || '#'} sx={{ display: 'block', flexShrink: 0 }}>
              <Box
                component="img"
                src={event.image}
                alt={event.title}
                sx={{ width: 160, height: 'auto', borderRadius: 1 }}
              />
            </MuiLink>
          )}

          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: '24px',
                fontWeight: 400,
                color: '#142434',
                mb: 1,
              }}
            >
              {event.title}
            </Typography>

            <Typography
              sx={{
                fontFamily: 'PT Sans, sans-serif',
                fontSize: '14px',
                color: '#142434',
                mb: 0.5,
              }}
            >
              {formattedDate}
              {locationText}
            </Typography>

            {eventTypeText && (
              <Typography
                sx={{
                  fontFamily: 'PT Sans, sans-serif',
                  fontSize: '14px',
                  color: '#142434',
                  mb: 1,
                }}
              >
                {eventTypeText}
              </Typography>
            )}

            {event.extLink && (
  <MuiLink
    href={event.extLink}
    target="_blank"
    rel="noopener noreferrer"
    sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: 14, wordBreak: 'break-all', color: '#489596' }} // ensures long URLs wrap
  >
    {event.extLink} {/* Display the actual link text */}
  </MuiLink>
)}

          </Box>
        </Box>

        {/* Section 2: Markdown main content */}
        <Box sx={{ mt: 4 }}>
          <ReactMarkdown
            components={{
              h1: ({ node, ...props }) => (
                <Typography
                  variant="h4"
                  sx={{ fontFamily: 'Poppins, sans-serif', color: '#142434', mb: 2 }}
                  {...props}
                />
              ),
              h2: ({ node, ...props }) => (
                <Typography
                  variant="h5"
                  sx={{ fontFamily: 'Poppins, sans-serif', color: '#142434', mb: 2 }}
                  {...props}
                />
              ),
              h3: ({ node, ...props }) => (
                <Typography
                  variant="h6"
                  sx={{ fontFamily: 'Poppins, sans-serif', color: '#142434', mb: 1 }}
                  {...props}
                />
              ),
              p: ({ node, ...props }) => (
                <Typography
                  sx={{
                    fontFamily: 'PT Sans, sans-serif',
                    fontSize: '16px',
                    color: '#142434',
                    mb: 2,
                  }}
                  {...props}
                />
              ),
              a: ({ node, ...props }) => (
                <MuiLink
                  {...props}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: '#489596', textDecoration: 'underline' }}
                />
              ),
              li: ({ node, ...props }) => (
                <li>
                  <Typography
                    sx={{
                      fontFamily: 'PT Sans, sans-serif',
                      fontSize: '16px',
                      color: '#142434',
                    }}
                    component="span"
                    {...props}
                  />
                </li>
              ),
              hr: ({ node, ...props }) => (
                <Box
                  component="hr"
                  sx={{ borderColor: '#489596', borderWidth: '0.5px', my: 2 }}
                  {...props}
                />
              ),
            }}
          >
            {event.mainContent}
          </ReactMarkdown>
        </Box>
      </Box>

      <Footer />
    </>
  );
};

export default EventPage;
